import {
  LogoutOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { history, useIntl, useModel } from "@umijs/max";
import type { MenuProps } from "antd";
import { Spin } from "antd";
import { createStyles } from "antd-style";
import React, { useState } from "react";
import { flushSync } from "react-dom";
import HeaderDropdown from "../HeaderDropdown";
import SwitchAdminModal from "../SwitchAdminModal";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel("@@initialState");
  const { currentUser, adminInfo } = initialState || {};

  // 优先显示姓名，如果为空则显示用户昵称
  const displayName =
    adminInfo?.name || currentUser?.name || currentUser?.userid || "";
  const adminType = adminInfo?.adminTypeDesc || "";

  return (
    <span className="anticon">
      {displayName}
      {adminType && ` (${adminType})`}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: "flex",
      height: "48px",
      marginLeft: "auto",
      overflow: "hidden",
      alignItems: "center",
      padding: "0 8px",
      cursor: "pointer",
      borderRadius: token.borderRadius,
      "&:hover": {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const intl = useIntl();
  const [switchModalVisible, setSwitchModalVisible] = useState(false);

  /**
   * 退出登录，清除 token 并跳转到登录页
   */
  const loginOut = async () => {
    // 清除所有本地存储的数据（包括 token）
    localStorage.clear();

    // 直接跳转到登录页，不保存当前 URL
    history.replace("/user/login");
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel("@@initialState");

  const onMenuClick: MenuProps["onClick"] = (event) => {
    const { key } = event;
    if (key === "logout") {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === "switch-admin") {
      setSwitchModalVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  if (!initialState.currentUser) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: "center",
            icon: <UserOutlined />,
            label: "个人中心",
          },
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: "个人设置",
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "switch-admin",
      icon: <SwapOutlined />,
      label: intl.formatMessage({ id: "menu.account.switch-admin" }),
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: "menu.account.logout" }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <SwitchAdminModal
        visible={switchModalVisible}
        onCancel={() => setSwitchModalVisible(false)}
      />
    </>
  );
};
